import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { CheckCircle, Clock, AlertCircle, RotateCcw, DollarSign, User, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { asArray } from "@/lib/utils";

type Payment = {
  id: number;
  kidId: number;
  kidName: string;
  amount: string;
  paymentType: string;
  month: string | null;
  dueDate: string;
  paidDate: string | null;
  status: "pending" | "pending_verification" | "paid" | string;
  paymentMethod: string | null;
  parentNote: string | null;
  notes: string | null;
};

type Tab = "pending" | "pending_verification" | "paid";

const TAB_LABELS: Record<Tab, string> = {
  pending: "Pending",
  pending_verification: "Awaiting Confirmation",
  paid: "Paid",
};

const formatAmount = (amount: string) => `$${parseFloat(amount).toFixed(2)}`;

const formatDate = (dateString: string | null) => {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export default function Payments() {
  const [activeTab, setActiveTab] = useState<Tab>("pending_verification");
  const [pendingIds, setPendingIds] = useState<Set<number>>(new Set());
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["/api/admin/payments"],
    queryFn: () => api.get("/api/admin/payments"),
  });

  const allPayments: Payment[] = asArray<Payment>(data?.data);

  const pendingPayments = allPayments.filter((p) => p.status === "pending");
  const awaitingPayments = allPayments.filter((p) => p.status === "pending_verification");
  const paidPayments = allPayments.filter((p) => p.status === "paid");

  const tabPayments: Record<Tab, Payment[]> = {
    pending: pendingPayments,
    pending_verification: awaitingPayments,
    paid: paidPayments,
  };

  const confirmMutation = useMutation({
    mutationFn: (paymentId: number) =>
      api.put(`/api/admin/payments/${paymentId}/confirm`, {}),
    onMutate: async (paymentId: number) => {
      await queryClient.cancelQueries({ queryKey: ["/api/admin/payments"] });
      const previousData = queryClient.getQueryData(["/api/admin/payments"]);
      queryClient.setQueryData(["/api/admin/payments"], (old: any) => {
        if (!old?.data) return old;
        return {
          ...old,
          data: old.data.map((p: Payment) =>
            p.id === paymentId
              ? { ...p, status: "paid", paidDate: new Date().toISOString() }
              : p
          ),
        };
      });
      return { previousData };
    },
    onError: (_err: any, _paymentId: any, context: any) => {
      if (context?.previousData) {
        queryClient.setQueryData(["/api/admin/payments"], context.previousData);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/payments"] });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (paymentId: number) =>
      api.put(`/api/admin/payments/${paymentId}/reject`, {}),
    onMutate: async (paymentId: number) => {
      await queryClient.cancelQueries({ queryKey: ["/api/admin/payments"] });
      const previousData = queryClient.getQueryData(["/api/admin/payments"]);
      queryClient.setQueryData(["/api/admin/payments"], (old: any) => {
        if (!old?.data) return old;
        return {
          ...old,
          data: old.data.map((p: Payment) =>
            p.id === paymentId
              ? { ...p, status: "pending", paymentMethod: null, parentNote: null }
              : p
          ),
        };
      });
      return { previousData };
    },
    onError: (_err: any, _paymentId: any, context: any) => {
      if (context?.previousData) {
        queryClient.setQueryData(["/api/admin/payments"], context.previousData);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/payments"] });
    },
  });

  const displayed = tabPayments[activeTab];

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900" data-testid="heading-payments">
          Payments
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Manage and confirm academy payments
        </p>
      </div>

      {/* Summary counts */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-yellow-700">{pendingPayments.length}</p>
          <p className="text-xs text-yellow-600 font-medium mt-0.5">Pending</p>
        </div>
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-orange-700">{awaitingPayments.length}</p>
          <p className="text-xs text-orange-600 font-medium mt-0.5">Awaiting</p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-green-700">{paidPayments.length}</p>
          <p className="text-xs text-green-600 font-medium mt-0.5">Paid</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg mb-6" role="tablist">
        {(["pending", "pending_verification", "paid"] as Tab[]).map((tab) => (
          <button
            key={tab}
            role="tab"
            aria-selected={activeTab === tab}
            data-testid={`tab-${tab}`}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-2 px-2 rounded-md text-xs md:text-sm font-medium transition-colors ${
              activeTab === tab
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-600 hover:text-gray-800"
            }`}
          >
            {TAB_LABELS[tab]}
            {tabPayments[tab].length > 0 && (
              <span
                className={`ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full text-xs ${
                  activeTab === tab ? "bg-blue-100 text-blue-700" : "bg-gray-200 text-gray-600"
                }`}
              >
                {tabPayments[tab].length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Payment list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12 gap-3">
          <div className="animate-spin w-6 h-6 border-4 border-blue-600 border-t-transparent rounded-full" />
          <span className="text-gray-600">Loading payments…</span>
        </div>
      ) : displayed.length === 0 ? (
        <div
          className="text-center py-12 text-gray-500"
          data-testid={`empty-${activeTab}`}
        >
          No {TAB_LABELS[activeTab].toLowerCase()} payments
        </div>
      ) : (
        <div className="space-y-3" data-testid={`list-${activeTab}`}>
          {displayed.map((payment) => (
            <div
              key={payment.id}
              data-testid={`payment-row-${payment.id}`}
              className="bg-white border border-gray-200 rounded-xl p-4 md:p-5 shadow-sm"
            >
              {/* Row header */}
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2 min-w-0">
                  <User className="h-4 w-4 text-gray-500 flex-shrink-0" />
                  <span className="font-semibold text-gray-900 truncate">
                    {payment.kidName}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <DollarSign className="h-4 w-4 text-gray-500" />
                  <span className="text-lg font-bold text-gray-900" data-testid={`payment-amount-${payment.id}`}>
                    {formatAmount(payment.amount)}
                  </span>
                </div>
              </div>

              {/* Details row */}
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 mb-3">
                {payment.month && (
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {payment.month}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  Due: {formatDate(payment.dueDate)}
                </span>
                <span className="capitalize">
                  {payment.paymentType.replace(/_/g, " ")}
                </span>
                {payment.paymentMethod && (
                  <span className="capitalize font-medium text-blue-600">
                    via {payment.paymentMethod}
                  </span>
                )}
              </div>

              {/* Parent note */}
              {payment.parentNote && (
                <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 mb-3 text-xs text-blue-800 italic">
                  "{payment.parentNote}"
                </div>
              )}

              {/* Action buttons — only for pending_verification */}
              {payment.status === "pending_verification" && (
                <div className="flex gap-2 pt-2 border-t border-gray-100">
                  <Button
                    size="sm"
                    data-testid={`confirm-btn-${payment.id}`}
                    onClick={() => {
                      setPendingIds((prev) => new Set(prev).add(payment.id));
                      confirmMutation.mutate(payment.id, {
                        onSettled: () =>
                          setPendingIds((prev) => {
                            const next = new Set(prev);
                            next.delete(payment.id);
                            return next;
                          }),
                      });
                    }}
                    disabled={pendingIds.has(payment.id)}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  >
                    <CheckCircle className="h-4 w-4 mr-1.5" />
                    Confirm Paid
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`reject-btn-${payment.id}`}
                    onClick={() => {
                      setPendingIds((prev) => new Set(prev).add(payment.id));
                      rejectMutation.mutate(payment.id, {
                        onSettled: () =>
                          setPendingIds((prev) => {
                            const next = new Set(prev);
                            next.delete(payment.id);
                            return next;
                          }),
                      });
                    }}
                    disabled={pendingIds.has(payment.id)}
                    className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                  >
                    <RotateCcw className="h-4 w-4 mr-1.5" />
                    Reset to Pending
                  </Button>
                </div>
              )}

              {/* Paid status indicator */}
              {payment.status === "paid" && (
                <div className="flex items-center gap-1.5 pt-2 border-t border-gray-100 text-xs text-green-700 font-medium">
                  <CheckCircle className="h-3.5 w-3.5" />
                  Paid {payment.paidDate ? `on ${formatDate(payment.paidDate)}` : ""}
                </div>
              )}

              {/* Pending status indicator */}
              {payment.status === "pending" && (
                <div className="flex items-center gap-1.5 pt-2 border-t border-gray-100 text-xs text-yellow-700 font-medium">
                  <Clock className="h-3.5 w-3.5" />
                  Awaiting parent payment
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Legacy "Record Payment" button kept for nav test compatibility */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <button
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
          data-testid="btn-record-payment"
          aria-label="Record Payment"
          onClick={() => {/* future: open create payment modal */}}
        >
          + Record Payment
        </button>
      </div>
    </div>
  );
}
